<template>
    <loading v-if="loading"></loading>
    <div v-else class="home">
        <div class="status-con">
            Account Status : <span :class="'has-text-' + status.className">{{status.name}}</span>
            <div v-if="statusCancelPending">
                Your account will be canceled on : <timestamp :value="$getDealer().cancelAt" display-in-utc/>
            </div>
        </div>
        <div
            v-if="stats.mostRecentOrderTm === null && $getSessionContext().paymentCollection"
            class="steps-con"
        >
            <h2 class="title is-size-3 is-upppercase has-text-centered">Getting Started</h2>
            <ul class="steps has-content-centered">
                <li class="steps-segment" :class="{ 'is-active' : stats.activePaymentMethods === 0 }">
                    <span class="steps-marker"><i class="fa fa-credit-card"></i></span>
                    <div class="steps-content">
                        <p class="title is-size-4 has-text-weight-light">Step 1</p>
                        <p class="step-message">Add a payment method to your account so you can pay for orders and configure your authorization rules</p>
                        <p class="m-t-md">
                            <router-link :to="{ name : $ROUTES.SETTINGS }" class="button is-link">Setup</router-link>
                        </p>
                    </div>
                </li>
                <li class="steps-segment" :class="{ 'is-active' : stats.activePaymentMethods !== 0}">
                    <span class="steps-marker"><i class="fa fa-user-secret"></i></span>
                    <div class="steps-content">
                        <p class="is-size-4 title has-text-weight-light">Step 2</p>
                        <p class="step-message">Get your API Token and provide it to your integrator to allow them to submit orders on your behalf</p>
                        <p class="m-t-md">
                            <router-link :to="{ name : $ROUTES.SETTINGS }" class="button is-link">Get Token</router-link>
                        </p>
                    </div>
                </li>
                <li class="steps-segment is-active">
                    <span class="steps-marker"><i class="fa fa-truck"></i></span>
                    <div class="steps-content">
                        <p class="is-size-4 title has-text-weight-light">Step 3</p>
                        <p class="step-message">Make a sale and get your first order imported<br>&nbsp;</p>
                        <p class="m-t-md">
                            <router-link :to="{ name : $ROUTES.ORDERS_INDEX }" class="button is-link">View Orders</router-link>
                        </p>
                    </div>
                </li>
            </ul>
        </div>
        <div>

            <div class="flex-con is-grow is-equal lg-pad-lr small-as-blocks is-start">
                <div>
                    <div class="last-7">
                        {{$store.state.dealer_portal.brandName}}
                    </div>
                    <div v-if="$store.state.dealer_portal.businessEmail" class="stat">
                        <h5 class="title">Email</h5>
                        <div class="info-format">{{$store.state.dealer_portal.businessEmail}}</div>
                    </div>
                    <div v-if="$store.state.dealer_portal.businessPhone" class="stat">
                        <h5 class="title">Phone</h5>
                        <div class="info-format">{{$store.state.dealer_portal.businessPhone}}</div>
                    </div>
                    <div
                        v-if="$store.state.dealer_portal.settings.program_details"
                        v-html="$store.state.dealer_portal.settings.program_details"
                    >
                    </div>
                </div>
                <div v-if="stats.mostRecentOrderTm !== null" class="has-text-right">
                    <div class="last-7">Last 7 Days</div>
                    <div class="stat">
                        <h2 class="title">Unauthorized Orders</h2>
                        <number class="number-format" :value="stats.totalUnauthorized"></number>
                    </div>
                    <div class="stat">
                        <h2 class="title">Total Orders</h2>
                        <number class="number-format" :value="stats.totalOrders"></number>
                    </div>
                    <div class="stat">
                        <h2 class="title">Total <default-currency-symbol/>  Value</h2>
                        <money class="number-format" :value="stats.totalValue || 0"></money>
                    </div>
                    <div class="stat">
                        <h2 class="title">Last Order Imported</h2>
                        <timestamp class="time-format" :value="stats.mostRecentOrderTm" :is-full="true" or="N/A"></timestamp>
                    </div>
                </div>
            </div>

            <div v-if="showInventorySourceUpsell" class="message is-link" style="margin-top:80px;">
                <div class="message-header">
                    Inventory Source Automation Services
                </div>
                <div class="message-body">
                    <p>
                        If you are looking to get access to our products, please
                        <a v-if="inventorySourceSupplierId" :href="`https://app.inventorysource.com/#/services/?vid=${inventorySourceSupplierId}`" target="_blank">
                            purchase an integration
                        </a>
                        <span v-else>
                            purchase an integration
                        </span>
                        with our technology and integration partner,
                        <a href="https://inventorysource.com" target="_blank">Inventory Source</a>.
                        With Inventory Source, you will be able to push product data to your site, automate inventory and price changes, process purchase orders to us, and automate back the shipment tracking.
                    </p>
                    <p v-if="inventorySourcePromoCode" class="m-t-sm">
                        Feel free to use the promo code: <span class="tag is-light">{{inventorySourcePromoCode}}</span> at checkout via your Inventory Source account on the Add Integration tab for 20% off of your first month.
                    </p>
                    <p class="m-t-sm">
                        Happy Selling!
                    </p>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import Dealer from '../api/Dealer';
import StatusUtil from "../util/StatusUtil";
import AccountSettingsMixin from "../mixins/AccountSettingsMixin";
import DefaultCurrencySymbol from "../elements/DefaultCurrencySymbol";

export default {

    name : 'home',
    components : {DefaultCurrencySymbol},
    mixins : [AccountSettingsMixin],

    beforeMount () {
        Dealer.getDashboardStats().then(response => {
            this.stats = response.data;
            this.loading = false;
        })
    },

    computed : {
        status () {
            return StatusUtil.DEALER.findById(this.$getDealer().accountDealerStatusId);
        },
        statusCancelPending () {
            return this.status.id === StatusUtil.DEALER.CANCEL_PENDING.id;
        },
    },

    data () {
        return {
            loading : true,
            stats : {
                totalOrders : 0,
                totalValue : null,
                mostRecentOrderTm : null,
                totalUnauthorized : 0,
                activePaymentMethods : 0,
            },
        };
    },

}
</script>

<style lang="scss" scoped>

    @import '../assets/css/_theme';
    @import "~bulma-extensions/bulma-steps/src/sass/index";
    @import "~bulma-steps-component/bulma-steps";

    .status-con {
        padding:0 0 15px 0;
        border-bottom:2px solid $white-ter;
        margin-bottom:20px;
        font-size:16px;
        color:$grey;
        span {
            text-transform: uppercase;
        }
    }

    .steps-con {
        border-bottom:2px solid $white-ter;
        margin-bottom:20px;
        padding:20px 0;
    }

    .last-7 {
        font-size:16px;
        color:$grey;
        text-transform: uppercase;
        margin-bottom:20px;
    }

    .stat {
        margin:0 auto 15px auto;
        padding:10px 0;
        .title {
            font-size:18px;
            //border-bottom:2px solid $white-bis;
            padding-bottom:5px;
            margin-bottom:2.5px;
        }
        .number-format {
            font-size:30px;
            font-weight:bold;
        }

        .time-format {
            font-size:20px;
            font-weight:bold;
        }

        .info-format {
            font-size:16px;
            font-weight:bold;
        }
    }

    .last-order {
        text-align:center;
        padding:40px 0;
        border-top:1px solid $white-ter;
        border-bottom:1px solid $white-ter;
        margin:40px 0;
        span {
            font-size:22px;
        }
    }

    .card-footer-item {
        .title, div {
            display:block;
        }
        .title {
            margin:0 10px 0 0;
        }
    }

    .step-message {
        max-width:80%;
        margin:0 auto;
    }

</style>
